/**
 * @fileoverview Edit User State field.
 * @description The State picker: All India (no state) or one of the catalogue's states. While the
 * catalogue is loading, or if it failed to load, the picker is disabled and not tied to the form,
 * so it only shows the user's current State and cannot be changed (with only All India on offer a
 * save could silently clear the user's State); it says why. Once the catalogue has loaded it is
 * the form's State field.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useEditUserStateField } from '@/features/users/hooks/useEditUserStateField'
import SelectField from '@/shared/components/molecules/SelectField'

import type { EditUserFormValues } from '@/features/users/schemas/edit-user-form.schema'
import type { UserDetail } from '@/features/users/types/users.types'
import type { UseFormReturn } from 'react-hook-form'

/**
 * @component
 * @param {{ form: UseFormReturn<EditUserFormValues>; user: UserDetail }} props - The form and the
 * user being edited.
 * @param {UseFormReturn<EditUserFormValues>} props.form - The edit form.
 * @param {UserDetail} props.user - The user being edited.
 */
function EditUserStateField({
  form,
  user,
}: {
  form: UseFormReturn<EditUserFormValues>
  user: UserDetail
}) {
  const { t } = useTranslation()
  const { options, isLoading, isError } = useEditUserStateField(user)
  const label = t('sales.userManagement.editUserForm.fields.state')

  if (isLoading || isError) {
    return (
      <div>
        <SelectField
          id="editUserState"
          data-testid="edit-user-state-select"
          label={label}
          options={options}
          defaultValue={user.state?.stateId ?? ''}
          disabled
        />
        {isLoading && (
          <p className="mt-1.5 text-[12px] text-slate" data-testid="edit-user-state-loading">
            {t('sales.userManagement.editUserForm.fields.stateLoading')}
          </p>
        )}
        {isError && (
          <p className="mt-1.5 text-[12px] text-danger" data-testid="edit-user-state-error-message">
            {t('sales.userManagement.editUserForm.loadStatesError')}
          </p>
        )}
      </div>
    )
  }

  return (
    <div>
      <SelectField
        id="editUserState"
        data-testid="edit-user-state-select"
        label={label}
        options={options}
        {...form.register('stateId')}
      />
    </div>
  )
}

export default EditUserStateField
